import React, { useState } from 'react';
import {
  Box,
  Image,
  IconButton,
  HStack,
  VStack,
  Text,
  useColorModeValue,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { FaChevronLeft, FaChevronRight, FaExpand } from 'react-icons/fa';

// Helper per ottenere le immagini dalla galleria
const getImages = (bazar) => {
  try {
    // Prova diverse strutture possibili - controlla PRIMA bazar.galleria direttamente se è un array
    let galleria = [];
    if (Array.isArray(bazar.galleria) && bazar.galleria.length > 0) {
      galleria = bazar.galleria;
    } else if (
      Array.isArray(bazar.attributes?.galleria) &&
      bazar.attributes.galleria.length > 0
    ) {
      galleria = bazar.attributes.galleria;
    } else if (
      bazar.attributes?.galleria?.data &&
      Array.isArray(bazar.attributes.galleria.data)
    ) {
      galleria = bazar.attributes.galleria.data;
    } else if (bazar.galleria?.data && Array.isArray(bazar.galleria.data)) {
      galleria = bazar.galleria.data;
    }

    return galleria
      .map((img) => {
        try {
          // Gestisce diverse strutture di Strapi (v4 e v5)
          let imageData = img;
          if (img.attributes) {
            imageData = img.attributes;
          } else if (img.data && img.data.attributes) {
            imageData = img.data.attributes;
          }

          // Prova diversi percorsi per l'URL
          const url =
            imageData.url ||
            imageData.formats?.large?.url ||
            imageData.formats?.medium?.url ||
            imageData.formats?.small?.url ||
            imageData.formats?.thumbnail?.url;

          if (url) {
            // Se l'URL inizia già con http/https, restituiscilo così com'è
            if (url.startsWith('http://') || url.startsWith('https://')) {
              return url;
            }

            // Se l'URL inizia con /uploads, potrebbe funzionare come relativo (tramite proxy in develop)
            // In produzione, se Strapi è su dominio diverso, serve l'URL completo
            const baseUrl =
              process.env.REACT_APP_STRAPI_API_URL ||
              process.env.REACT_APP_STRAPI_URL;

            // Se abbiamo un baseUrl configurato, usalo
            if (baseUrl) {
              const cleanBaseUrl = baseUrl.replace(/\/$/, '');
              const cleanImageUrl = url.startsWith('/') ? url : `/${url}`;
              return `${cleanBaseUrl}${cleanImageUrl}`;
            }

            // Se non c'è baseUrl, prova come URL relativo (funziona se Strapi è sullo stesso dominio o tramite proxy)
            return url.startsWith('/') ? url : `/${url}`;
          }
        } catch (error) {
          console.error('Errore nel recupero immagine dalla galleria:', error);
        }

        return null;
      })
      .filter(Boolean);
  } catch (error) {
    console.error('Errore nel recupero galleria:', error);
    return [];
  }
};

export default function BazarGallery({ bazar }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const images = getImages(bazar);

  const bgColor = useColorModeValue('gray.100', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  if (images.length === 0) {
    return (
      <Box
        width="100%"
        height="400px"
        bg={bgColor}
        borderRadius="lg"
        display="flex"
        alignItems="center"
        justifyContent="center"
        border="1px solid"
        borderColor={borderColor}
      >
        <Box
          textAlign="center"
          color={useColorModeValue('gray.400', 'gray.500')}
        >
          <Text fontSize="sm">Nessuna immagine disponibile</Text>
        </Box>
      </Box>
    );
  }

  const handlePrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  const currentImage = images[currentIndex];

  return (
    <>
      <VStack spacing={4} align="stretch">
        {/* Immagine principale */}
        <Box
          position="relative"
          width="100%"
          height="500px"
          bg={bgColor}
          borderRadius="lg"
          overflow="hidden"
          border="1px solid"
          borderColor={borderColor}
          cursor="pointer"
          onClick={onOpen}
        >
          <Image
            src={currentImage}
            alt={`Immagine ${currentIndex + 1} di ${images.length}`}
            width="100%"
            height="100%"
            objectFit="contain"
          />

          {/* Navigazione */}
          {images.length > 1 && (
            <>
              <IconButton
                icon={<FaChevronLeft />}
                aria-label="Immagine precedente"
                position="absolute"
                left={2}
                top="50%"
                transform="translateY(-50%)"
                onClick={handlePrevious}
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.800' }}
              />
              <IconButton
                icon={<FaChevronRight />}
                aria-label="Immagine successiva"
                position="absolute"
                right={2}
                top="50%"
                transform="translateY(-50%)"
                onClick={handleNext}
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.800' }}
              />
              <IconButton
                icon={<FaExpand />}
                aria-label="Espandi"
                position="absolute"
                top={2}
                right={2}
                onClick={onOpen}
                bg="blackAlpha.600"
                color="white"
                _hover={{ bg: 'blackAlpha.800' }}
              />
            </>
          )}

          {/* Indicatore immagine corrente */}
          {images.length > 1 && (
            <Box
              position="absolute"
              bottom={2}
              left="50%"
              transform="translateX(-50%)"
              bg="blackAlpha.700"
              color="white"
              px={3}
              py={1}
              borderRadius="md"
              fontSize="sm"
            >
              {currentIndex + 1} / {images.length}
            </Box>
          )}
        </Box>

        {/* Thumbnail navigation */}
        {images.length > 1 && (
          <HStack spacing={2} overflowX="auto" pb={2}>
            {images.map((img, index) => {
              const imageKey = `${img}-${index}`;
              return (
                <Box
                  key={imageKey}
                  width="80px"
                  height="80px"
                  flexShrink={0}
                  borderRadius="md"
                  overflow="hidden"
                  border="2px solid"
                  borderColor={
                    index === currentIndex ? 'brand.100' : borderColor
                  }
                  cursor="pointer"
                  onClick={() => handleThumbnailClick(index)}
                  _hover={{ borderColor: 'brand.100' }}
                  transition="border-color 0.2s"
                >
                  <Image
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                  />
                </Box>
              );
            })}
          </HStack>
        )}
      </VStack>

      {/* Modal lightbox */}
      <Modal isOpen={isOpen} onClose={onClose} size="full" isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none">
          <ModalCloseButton color="white" zIndex={10} />
          <ModalBody
            p={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box position="relative" maxW="90vw" maxH="90vh">
              <Image
                src={currentImage}
                alt={`Immagine ${currentIndex + 1} di ${images.length}`}
                maxW="100%"
                maxH="90vh"
                objectFit="contain"
              />

              {images.length > 1 && (
                <>
                  <IconButton
                    icon={<FaChevronLeft />}
                    aria-label="Immagine precedente"
                    position="absolute"
                    left={4}
                    top="50%"
                    transform="translateY(-50%)"
                    onClick={handlePrevious}
                    bg="blackAlpha.600"
                    color="white"
                    size="lg"
                    _hover={{ bg: 'blackAlpha.800' }}
                  />
                  <IconButton
                    icon={<FaChevronRight />}
                    aria-label="Immagine successiva"
                    position="absolute"
                    right={4}
                    top="50%"
                    transform="translateY(-50%)"
                    onClick={handleNext}
                    bg="blackAlpha.600"
                    color="white"
                    size="lg"
                    _hover={{ bg: 'blackAlpha.800' }}
                  />
                  <Box
                    position="absolute"
                    bottom={4}
                    left="50%"
                    transform="translateX(-50%)"
                    bg="blackAlpha.700"
                    color="white"
                    px={4}
                    py={2}
                    borderRadius="md"
                    fontSize="md"
                  >
                    {currentIndex + 1} / {images.length}
                  </Box>
                </>
              )}
            </Box>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
}
